import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import './styles/game-screen.css';
import './styles/player-screen.css';
import RoomSetup from './components/game-setup/RoomSetup';
import JoinRoom from './components/game-setup/JoinRoom';
import PremiumHostSetup from './components/game-setup/PremiumHostSetup';
import PhoneOnlySetup from './components/game-setup/PhoneOnlySetup';
import HostDisplay from './components/game-screens/HostDisplay';
import { BonusHostScreen } from './components/game-screens/bonus';
import PlayerDisplay from './components/game-screens/PlayerDisplay';
import GameSelection from './components/lobby/GameSelection';
import { ConnectionStatus, SoundDrawer, HostControlDrawer, GamePausedOverlay, FeelingAloneAiProvider } from './components/shared';
import { createWebSocketAdapter, WebSocketAdapter } from './utils/WebSocketAdapter';
import { ToastProvider, useToast } from './components/shared/ToastContext';
import { config, ALL_GAMES, appVariant } from './config';
import { isMonthlyFreeGame } from './config/monthlyFreeGames';
import RoomSessionStorage from './utils/sessionStorage';
import { emitRoomRestore } from './utils/restoreRoomSession';
import { useReconnection } from './hooks/useReconnection';
import { useSocketEvents } from './hooks/useSocketEvents';
import { getRoomCodeFromUrl, getHostCodeFromUrl, clearQueryParamFromUrl, generateRoomCode } from './utils/roomCode';
import {
  applyRoomBrandingBackground,
  applyRoomBrandingColors,
  clearRoomBrandingBackground,
  clearRoomBrandingColors,
} from './utils/roomBranding';
import {
  isHostEntryPath,
  isImplicitHostCreatePath,
  isJoinEntryPath,
  isPaymentSuccessPath,
  getCurrentPath,
  navigateToHost,
  navigateTo,
  navigateToRoot,
  clearEntryRouteFromUrl,
  consumeJoinEntryRoute,
  shouldShowJoinScreen,
  RESTORE_GIVE_UP_MS,
  useEntryPath,
  ENTRY_ROUTES,
  ENTRY_ROUTE_CHANGE_EVENT,
} from './utils/entryRoutes';
import RoomLoadingShell from './components/shared/RoomLoadingShell';
import { setSoundsMutedForSession } from './utils/sounds';
import PaymentSuccess from './components/payment/PaymentSuccess';
import PremiumPage from './components/menu/PremiumPage';
import MenuPageWrapper from './components/menu/MenuPageWrapper';
import ConfirmModal from './components/shared/ConfirmModal';
import PartyAgeGate, { isPartyAgeVerified } from './components/game-setup/PartyAgeGate';
import { useTranslation } from './i18n';
import type { RoomState } from './types/room';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

function AppContent() {
  const { t } = useTranslation();
  const [socket, setSocket] = useState<WebSocketAdapter | null>(null);
  const [roomState, setRoomState] = useState<RoomState | null>(null);
  const [connected, setConnected] = useState(false);
  const [showJoinRoom, setShowJoinRoom] = useState(() => isJoinEntryPath() || !!getRoomCodeFromUrl());
  const [isPremiumJoin, setIsPremiumJoin] = useState(false);
  const [showPhoneOnlySetup, setShowPhoneOnlySetup] = useState(false);
  const [showPremiumHostSetup, setShowPremiumHostSetup] = useState(false);
  const [isHost, setIsHost] = useState(false);
  const [myPlayerId, setMyPlayerId] = useState<string | null>(null);
  const [hostIsPlaying, setHostIsPlaying] = useState(false);
  const [phoneOnlyPlayerInfo, setPhoneOnlyPlayerInfo] = useState<{ name: string; avatar: string } | null>(null);
  const pendingStartGameRef = useRef<{ gameType: string } | null>(null);
  // Use ref to track current roomState to avoid stale closure issues
  const roomStateRef = useRef<RoomState | null>(null);
  // Track when user is intentionally leaving to prevent stray room-state events from re-establishing connection
  const isLeavingRef = useRef<boolean>(false);
  /** After confirmed leave, block auto session restore / ?host= recreate until Host/Join/Have a code? */
  const suppressSessionRestoreRef = useRef<boolean>(false);
  const historyGuardActiveRef = useRef<boolean>(false);
  // After WS disconnect, force join-room even if stale roomState still exists (phone lock soft reconnect)
  const needsRejoinRef = useRef<boolean>(false);
  // Track connection errors to detect when server is unreachable
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const connectionErrorAttemptsRef = useRef<number>(0);
  const [showPremiumPage, setShowPremiumPage] = useState(false);
  const [confirmLeaveOpen, setConfirmLeaveOpen] = useState(false);
  /** True while processing ?host= / /host deep link — show loader instead of RoomSetup */
  const [creatingHostFromUrl, setCreatingHostFromUrl] = useState(
    () => !!getHostCodeFromUrl() || isHostEntryPath()
  );
  const [partyAgeVerified, setPartyAgeVerified] = useState(
    () => appVariant !== 'party' || isPartyAgeVerified()
  );
  /** When true, game setup shows tiles immediately instead of waiting up to 2.5s for restore */
  const [restoreAttemptSettled, setRestoreAttemptSettled] = useState(false);
  const { showToast } = useToast();
  // Track failed room creation attempts to prevent retries
  const failedRoomsRef = useRef<Set<string>>(new Set());
  // Ref to current socket so we can close it when reconnecting to correct DO (Cloudflare prod)
  const socketRef = useRef<WebSocketAdapter | null>(null);
  socketRef.current = socket;
  const autoHostAttemptedRef = useRef(false);
  /** Host create-room payload to emit after WebSocket connects to the target room DO. */
  const pendingCreateRoomRef = useRef<{
    gameType: string;
    roomCode: string;
    hostToken?: string;
    previousRoomName?: string;
  } | null>(null);
  const currentPath = useEntryPath();
  // Ref to latest handleGoHome so handleLoadingTimeout can call it without being in the dependency array
  const handleGoHomeRef = useRef<() => void>(() => {});

  // Reconnect to backend with room in URL so the worker routes to the correct Durable Object (prod)
  const handleReconnectToRoom = useCallback((roomName: string) => {
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }
    setConnected(false);
    setRestoreAttemptSettled(false);
    setSocket(createWebSocketAdapter(BACKEND_URL, roomName));
  }, []);

  /** Connect to the room's DO, then create-room once the socket is open (Cloudflare prod). */
  const scheduleCreateRoom = useCallback(
    (payload: {
      gameType: string;
      roomCode: string;
      hostToken?: string;
      previousRoomName?: string;
    }) => {
      const targetRoom = payload.roomCode.toUpperCase();
      const current = socketRef.current;
      if (current?.connected && current.routingRoom === targetRoom) {
        current.emit('create-room', payload);
        return;
      }
      pendingCreateRoomRef.current = payload;
      handleReconnectToRoom(targetRoom);
    },
    [handleReconnectToRoom]
  );

  const onMaxAttemptsReached = useCallback((savedIsHost: boolean) => {
    if (savedIsHost) {
      setShowJoinRoom(false);
      navigateToRoot(true);
    } else {
      setShowJoinRoom(true);
    }
  }, []);

  // Stable callback — must not be inline or useSocketEvents re-registers all handlers on every render
  // (which re-runs connect catch-up and spams "Connected to backend" in the lobby).
  const onHostDeepLinkSettled = useCallback(() => {
    setCreatingHostFromUrl(false);
  }, []);

  // Reconnection hook
  const {
    reconnecting,
    reconnectAttempts,
    reconnectTimeoutRef,
    startReconnection,
    clearReconnection,
    setReconnecting,
    setReconnectAttempts,
  } = useReconnection({
    socket,
    isLeavingRef,
    suppressSessionRestoreRef,
    roomStateRef,
    needsRejoinRef,
    onMaxAttemptsReached,
    showToast,
    setConnected,
    setShowJoinRoom,
    setRoomState,
  });

  // Apply theme on mount
  useEffect(() => {
    // Set theme attribute on document for CSS variable switching
    document.documentElement.setAttribute('data-theme', config.theme.name);
    // Also set document title
    document.title = config.appName;
  }, []);

  // Premium room branding accent + backdrop (cleared when leaving room / no branding)
  useEffect(() => {
    applyRoomBrandingColors(roomState?.branding);
    applyRoomBrandingBackground(roomState?.branding);
    return () => {
      clearRoomBrandingColors();
      clearRoomBrandingBackground();
    };
  }, [roomState?.branding]);

  // Expose socket connection state for e2e tests (wait for this before creating room)
  useEffect(() => {
    document.documentElement.dataset.socketConnected = connected ? 'true' : 'false';
  }, [connected]);

  // Expose in-room state for e2e reconnection tests
  useEffect(() => {
    document.documentElement.dataset.inRoom = roomState ? 'true' : 'false';
  }, [roomState]);

  // When host is playing on this device (same room), mute all sounds and hide drawer (no music/SFX from this phone)
  useEffect(() => {
    const mute = !!(roomState?.gameState === 'playing' && isHost && hostIsPlaying);
    setSoundsMutedForSession(mute);
    return () => setSoundsMutedForSession(false);
  }, [roomState?.gameState, isHost, hostIsPlaying]);

  // After mid-game refresh, local hostIsPlaying is lost — restore from stable room-state first.
  useEffect(() => {
    if (!isHost || !roomState || hostIsPlaying) return;
    if (roomState.gameState !== 'playing' && roomState.gameState !== 'finished') return;
    if (
      roomState.hostIsPlaying === true ||
      roomState.players.some((p) => p.isHost === true || p.id === roomState.hostId || p.socketId === roomState.hostId)
    ) {
      setHostIsPlaying(true);
    }
  }, [isHost, roomState, hostIsPlaying]);

  // Clear expired room data on mount before any reconnection attempts
  useEffect(() => {
    const savedData = RoomSessionStorage.getSavedRoomData();
    const isExpired = savedData.expiresAt ? Date.now() > savedData.expiresAt : false;
    if (isExpired && savedData.roomName) {
      console.log(`Clearing expired room data on mount: ${savedData.roomName} expired at ${new Date(savedData.expiresAt!).toISOString()}`);
      RoomSessionStorage.clearAll();
      // Ensure we show room setup page, not join room screen
      setShowJoinRoom(false);
      setRoomState(null);
    }
  }, []); // Run only on mount

  const savedRoomData = RoomSessionStorage.getSavedRoomData();
  const isSavedRoomExpired = savedRoomData.expiresAt != null && Date.now() > savedRoomData.expiresAt;
  const hasRestorableSession = !!(
    savedRoomData.roomName &&
    !isSavedRoomExpired &&
    (savedRoomData.hostToken || savedRoomData.playerName)
  );
  const isImplicitHostCreate =
    (isImplicitHostCreatePath(currentPath) || creatingHostFromUrl) && !getHostCodeFromUrl();
  // Show restore loader as soon as we have saved session — don't wait for socket connect
  const couldRestore = hasRestorableSession && !isImplicitHostCreate;

  // Give session restore a window before route-specific fallbacks (e.g. /host auto-create)
  useEffect(() => {
    if (!couldRestore || restoreAttemptSettled) return;
    const restoreMs = savedRoomData.hostToken ? 5000 : RESTORE_GIVE_UP_MS;
    const timer = setTimeout(() => setRestoreAttemptSettled(true), restoreMs);
    return () => clearTimeout(timer);
  }, [couldRestore, restoreAttemptSettled, savedRoomData.hostToken]);

  // After restore loader gives up, retry once if socket is connected but room-state never arrived
  useEffect(() => {
    if (!restoreAttemptSettled || roomState || !connected || !socket) return;
    if (isLeavingRef.current || suppressSessionRestoreRef.current) return;
    const saved = RoomSessionStorage.getSavedRoomData();
    const expired = saved.expiresAt ? Date.now() > saved.expiresAt : false;
    if (!saved.roomName || expired) return;
    emitRoomRestore(socket, saved, { roomName: saved.roomName });
  }, [restoreAttemptSettled, roomState, connected, socket]);

  // Reset restore flag when we leave setup (so next visit shows loader again)
  useEffect(() => {
    if (!couldRestore) {
      setRestoreAttemptSettled(false);
    }
  }, [couldRestore]);

  // /host from marketing: auto-create a room once restore is not pending
  useEffect(() => {
    if (!isHostEntryPath(currentPath) || roomState || isLeavingRef.current || suppressSessionRestoreRef.current) return;
    if (!socket || !connected) return;
    if (showPhoneOnlySetup || showPremiumHostSetup) return;
    if (getHostCodeFromUrl()) return; // premium ?host= handled separately
    if (couldRestore && !restoreAttemptSettled) return;
    if (autoHostAttemptedRef.current) return;

    const savedData = RoomSessionStorage.getSavedRoomData();
    const savedExpired = savedData.expiresAt ? Date.now() > savedData.expiresAt : false;
    const hasRestorableSession =
      !!savedData.roomName &&
      !savedExpired &&
      (!!savedData.hostToken || !!savedData.playerName);
    if (hasRestorableSession) return;

    autoHostAttemptedRef.current = true;
    setCreatingHostFromUrl(true);
    const defaultGameType = config.games[0]?.id || 'trivia';
    console.log('Auto-creating room from /host entry route');
    scheduleCreateRoom({ gameType: defaultGameType, roomCode: generateRoomCode() });
  }, [
    socket,
    connected,
    roomState,
    couldRestore,
    restoreAttemptSettled,
    showPhoneOnlySetup,
    showPremiumHostSetup,
    currentPath,
    scheduleCreateRoom,
  ]);

  // Emit pending create-room after socket connects to the target room DO
  useEffect(() => {
    if (!socket || !connected || !pendingCreateRoomRef.current) return;
    const pending = pendingCreateRoomRef.current;
    pendingCreateRoomRef.current = null;
    socket.emit('create-room', pending);
  }, [socket, connected]);

  // Check for room parameter, host parameter, premium-host parameter, or payment success in URL on mount
  useEffect(() => {
    // Track if we've already attempted to create a room to prevent spam
    const attemptedRooms = new Set<string>();
    let lastAttemptTime = 0;
    const MIN_ATTEMPT_INTERVAL = 2000; // Only attempt once per 2 seconds per room
    
    const checkUrlParams = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');
      const path = window.location.pathname;
      const isAiTopUpReturn =
        urlParams.get('ai_topup') === '1' || urlParams.get('ai_topup_canceled') === '1';

      // AI prompt top-up returns to host lobby — not the premium package PaymentSuccess page
      if (isAiTopUpReturn) {
        if (isLeavingRef.current || suppressSessionRestoreRef.current) {
          clearQueryParamFromUrl('host');
          const next = new URLSearchParams(urlParams);
          next.delete('session_id');
          next.delete('ai_topup');
          next.delete('ai_topup_canceled');
          next.delete('host');
          const search = next.toString();
          window.history.replaceState({}, '', `${window.location.pathname}${search ? `?${search}` : ''}`);
          setCreatingHostFromUrl(false);
          return;
        }
        const next = new URLSearchParams(urlParams);
        next.delete('session_id');
        next.delete('ai_topup');
        next.delete('ai_topup_canceled');
        const search = next.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${search ? `?${search}` : ''}`);
        // Fall through so ?host= can recreate/reconnect the room with updated credits
      } else if (isPaymentSuccessPath(path) || sessionId) {
        // Payment success will be handled by the PaymentSuccess component
        return;
      }

      // /join entry route — show join screen, then clear path so refresh lands on root
      if (isJoinEntryPath(path) && !roomState) {
        setShowJoinRoom(true);
        consumeJoinEntryRoute();
      }

      // Legacy ?room= on any path — normalize to /?room= and show join screen
      const roomFromUrlEarly = getRoomCodeFromUrl();
      if (roomFromUrlEarly && !roomState && !isJoinEntryPath(path)) {
        navigateTo(ENTRY_ROUTES.ROOT, { replace: true, preserveSearch: true });
        setShowJoinRoom(true);
      }
      
      // Check for premium-host parameter (navigate to premium host setup)
      const premiumHost = urlParams.get('premium-host');
      if (premiumHost === 'true' && !roomState) {
        // Clear old non-premium room data when navigating to premium host setup
        // This prevents reconnection attempts to old rooms
        const savedData = RoomSessionStorage.getSavedRoomData();
        if (savedData.roomName && !savedData.expiresAt) {
          console.log(`Clearing old non-premium room data (${savedData.roomName}) when navigating to premium host setup`);
          RoomSessionStorage.clearAll();
        }
        setShowPremiumHostSetup(true);
        clearQueryParamFromUrl('premium-host');
        return;
      }
      
      // Check for host parameter (premium room creation)
      // Only create room if socket is connected and we're not already in a room
      // Also skip if we're showing join room screen (user might be trying to join, not create)
      const hostCodeFromUrl = getHostCodeFromUrl();
      if (hostCodeFromUrl && !roomState && !showJoinRoom && socket && connected) {
        // After intentional leave, stale history can resurrect ?host= — never auto-recreate
        if (isLeavingRef.current || suppressSessionRestoreRef.current) {
          console.log('Ignoring ?host= recreate - session restore suppressed after intentional leave');
          clearQueryParamFromUrl('host');
          setCreatingHostFromUrl(false);
          return;
        }
        setCreatingHostFromUrl(true);
        suppressSessionRestoreRef.current = false;
        // Don't retry if this room already failed
        if (failedRoomsRef.current.has(hostCodeFromUrl)) {
          clearQueryParamFromUrl('host');
          setCreatingHostFromUrl(false);
          return;
        }
        
        // Check if saved room data is expired - if so, clear it and don't try to create room
        // This prevents reconnecting to expired rooms when URL parameter matches expired room
        const savedData = RoomSessionStorage.getSavedRoomData();
        const isSavedRoomExpired = savedData.expiresAt ? Date.now() > savedData.expiresAt : false;
        if (isSavedRoomExpired && savedData.roomName === hostCodeFromUrl) {
          console.log(`Clearing expired room data: ${savedData.roomName} expired at ${new Date(savedData.expiresAt!).toISOString()}, URL parameter matches but room is expired - clearing and showing room setup`);
          RoomSessionStorage.clearAll();
          clearQueryParamFromUrl('host');
          setCreatingHostFromUrl(false);
          return; // Don't try to create room if it's expired
        }
        
        // If expired but different room, just clear the expired data
        if (isSavedRoomExpired && savedData.roomName && savedData.roomName !== hostCodeFromUrl) {
          console.log(`Clearing expired room data for different room: ${savedData.roomName} expired, creating new room ${hostCodeFromUrl}`);
          RoomSessionStorage.clearAll();
        }
        
        // Throttle: prevent multiple attempts for the same room within MIN_ATTEMPT_INTERVAL
        const now = Date.now();
        if (attemptedRooms.has(hostCodeFromUrl) && (now - lastAttemptTime) < MIN_ATTEMPT_INTERVAL) {
          // Already attempted recently, skip
          return;
        }
        
        // Mark as attempted and update timestamp
        attemptedRooms.add(hostCodeFromUrl);
        lastAttemptTime = now;
        
        // Create room as host with the specified premium room code
        // Check if we have a saved hostToken (for reconnection or room migration scenarios)
        // IMPORTANT: Don't require roomName to match - host might be moving from old room to new premium room
        // Re-fetch saved data after potential clearing
        const currentSavedData = RoomSessionStorage.getSavedRoomData();
        // Capture previous room name for host-moved-to-new-room (so backend can notify players in old room)
        const previousRoomName = currentSavedData.roomName && currentSavedData.hostToken && currentSavedData.roomName !== hostCodeFromUrl
          ? currentSavedData.roomName
          : undefined;
        
        // Clear old room data if it doesn't match the admin room code we're trying to create
        // This prevents reconnection attempts to old rooms
        if (currentSavedData.roomName && currentSavedData.roomName !== hostCodeFromUrl) {
          console.log(`Clearing old room data (${currentSavedData.roomName}) - creating new admin room (${hostCodeFromUrl})`);
          // Only preserve hostToken if it's for a premium room (has expiration)
          const isPremiumRoom = currentSavedData.expiresAt !== null;
          const shouldPreserveToken = !!currentSavedData.hostToken && isPremiumRoom;
          const preservedToken = shouldPreserveToken ? currentSavedData.hostToken : undefined;
          RoomSessionStorage.clearAll();
          // Restore hostToken only if it was for a premium room
          if (preservedToken) {
            RoomSessionStorage.setHostToken(preservedToken);
            RoomSessionStorage.setIsHost(true);
          }
        }
        
        // Re-fetch saved data after potential clearing
        const finalSavedData = RoomSessionStorage.getSavedRoomData();
        const hostToken = finalSavedData.hostToken || undefined;
        const defaultGameType = config.games[0]?.id || 'trivia';
        console.log(`Creating premium room with code: ${hostCodeFromUrl}${hostToken ? ` (with hostToken from previous room ${finalSavedData.roomName || 'unknown'})` : ''}${previousRoomName ? ` (notifying players in ${previousRoomName})` : ''}`);
        scheduleCreateRoom({
          gameType: defaultGameType,
          roomCode: hostCodeFromUrl,
          hostToken,
          previousRoomName,
        });
        // URL param will be cleared in handleRoomCreated when room is successfully created
        // or in handleRoomError if creation fails
        return;
      }
      
      // Check for regular room parameter (player join) — already on /join or redirected above
      const roomFromUrl = getRoomCodeFromUrl();
      if (roomFromUrl && !roomState && !showJoinRoom) {
        setShowJoinRoom(true);
      }
    };
    
    const handlePopState = () => {
      const path = window.location.pathname;
      if (isJoinEntryPath(path)) {
        setShowJoinRoom(true);
        consumeJoinEntryRoute();
      } else if (!isJoinEntryPath(path)) {
        setShowJoinRoom(false);
        setIsPremiumJoin(false);
      }
      if (!isHostEntryPath(path)) {
        autoHostAttemptedRef.current = false;
      }
      checkUrlParams();
    };
    // Check immediately
    checkUrlParams();
    
    window.addEventListener('popstate', handlePopState);
    
    // Also check periodically in case URL changes via pushState/replaceState
    // (which doesn't trigger popstate) - but only if we're not in a room yet
    // This helps with test scenarios where URL is set after page load
    let intervalId: NodeJS.Timeout | null = null;
    if (!roomState) {
      intervalId = setInterval(() => {
        // Only check if we're still not in a room (to avoid unnecessary checks)
        if (!roomStateRef.current) {
          checkUrlParams();
        } else {
          // Room was created, stop checking
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
        }
      }, 2000); // Check every 2 seconds (slower to prevent spam)
    }
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, connected, roomState]); // Include roomState to prevent creating room if we're already in one

  // Keep ref in sync with state
  useEffect(() => {
    roomStateRef.current = roomState;
  }, [roomState]);

  // Handle page unload - DON'T send leave-room on refresh
  // On page refresh, let the socket disconnect naturally, which will trigger handleDisconnect
  // This marks the host as disconnected but keeps the room alive for 1 minute for reconnection
  // Only send leave-room when user explicitly clicks back/leave button (handled elsewhere)
  useEffect(() => {
    const clearStaleLeavingFlag = () => {
      // beforeunload can set isLeavingRef without an actual unload (tab switch / cancelled refresh).
      // If we're still in a room, allow room-state again.
      if (roomStateRef.current) {
        isLeavingRef.current = false;
      }
    };

    const handleBeforeUnload = () => {
      // DO NOT send leave-room on beforeunload (refresh/close)
      // This allows the room to stay alive for 1 minute when host refreshes
      // The socket disconnect will trigger handleDisconnect which properly marks host as disconnected
      // but keeps the room alive for reconnection
    };

    const handlePageShow = () => {
      clearStaleLeavingFlag();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [isHost, roomState]);

  // Handle visibility change (browser tab goes to background/foreground, screen goes off)
  // Throttled to prevent excessive requests
  useEffect(() => {
    let lastRequestTime = 0;
    const THROTTLE_MS = 5000; // Only allow one request per 5 seconds
    
    const requestRoomState = () => {
      const now = Date.now();
      // Throttle: only request if at least 5 seconds have passed since last request
      if (now - lastRequestTime < THROTTLE_MS) {
        console.log('Throttled: skipping room state request (too soon after last request)');
        return;
      }
      
      if (!document.hidden && socket && roomState && connected && !reconnecting) {
        // Only request room state if:
        // 1. Tab is visible
        // 2. Socket exists
        // 3. We have room state
        // 4. We're connected (not disconnected)
        // 5. We're not currently reconnecting
        // 6. We have sessionStorage entry (room wasn't cleared)
        // 7. Game is not actively playing (to avoid interfering with question flow)
        //    - If game is playing, only request if we don't have a current question (reconnection scenario)
        const savedData = RoomSessionStorage.getSavedRoomData();
        if (savedData.roomName && savedData.roomName === roomState.roomName) {
          // Request room state when tab becomes visible so player timer can sync with host
          // (useGameState syncs timeRemaining for same question from server, so no reset to max)
          lastRequestTime = now;
          console.log('Tab became visible, requesting fresh room state');
          // Include hostToken whenever present (host play-too clears session isHost)
          if (savedData.hostToken) {
            socket.emit('get-room-state', { roomName: roomState.roomName, hostToken: savedData.hostToken });
          } else {
            socket.emit('get-room-state', { roomName: roomState.roomName });
          }
        } else {
          console.log('Tab became visible but sessionStorage missing or mismatched - not requesting room state');
        }
      }
    };

    const clearStaleLeavingFlag = () => {
      if (roomStateRef.current) {
        isLeavingRef.current = false;
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        clearStaleLeavingFlag();
      }
      requestRoomState();
      // Also notify server that player is back online so host gets updated player list
      if (document.visibilityState === 'visible' && socket && roomState && connected && !reconnecting) {
        const savedData = RoomSessionStorage.getSavedRoomData();
        if (savedData.roomName && savedData.roomName === roomState.roomName && savedData.playerName) {
          if (needsRejoinRef.current) {
            // Soft reconnect: player-back-online alone cannot re-add after lobby remove
            console.log('Player back online (visibility) — needsRejoin, emitting join-room');
            socket.emit('join-room', {
              roomName: savedData.roomName,
              playerName: savedData.playerName,
              avatar: savedData.avatar || undefined,
            });
          } else {
            console.log('Player back online - notifying server to update host player list');
            socket.emit('player-back-online', { roomName: savedData.roomName });
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also handle page focus (when user switches back to tab)
    // Use same throttled function
    const handleFocus = () => {
      clearStaleLeavingFlag();
      requestRoomState();
      // Also notify server that player is back online so host gets updated player list
      if (socket && roomState && connected && !reconnecting) {
        const savedData = RoomSessionStorage.getSavedRoomData();
        if (savedData.roomName && savedData.roomName === roomState.roomName && savedData.playerName) {
          if (needsRejoinRef.current) {
            console.log('Player back online (focus) — needsRejoin, emitting join-room');
            socket.emit('join-room', {
              roomName: savedData.roomName,
              playerName: savedData.playerName,
              avatar: savedData.avatar || undefined,
            });
          } else {
            console.log('Player back online (focus) - notifying server to update host player list');
            socket.emit('player-back-online', { roomName: savedData.roomName });
          }
        }
      }
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [socket, roomState, connected, reconnecting]);

  // Host-only fallback in lobby: keep waiting-room player list fresh even if a reconnect event is missed.
  useEffect(() => {
  // 10s is enough — join/leave events push room-state; polling is only a missed-event fallback.
    const HOST_WAITING_STATE_POLL_MS = 10_000;

    if (!socket || !roomState || !connected || reconnecting || !isHost || roomState.gameState !== 'waiting') {
      return;
    }

    const intervalId = setInterval(() => {
      const savedData = RoomSessionStorage.getSavedRoomData();
      if (!savedData.roomName || savedData.roomName !== roomState.roomName) {
        return;
      }

      if (savedData.hostToken) {
        socket.emit('get-room-state', { roomName: roomState.roomName, hostToken: savedData.hostToken });
      } else {
        socket.emit('get-room-state', { roomName: roomState.roomName });
      }
    }, HOST_WAITING_STATE_POLL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [socket, roomState, connected, reconnecting, isHost]);

  // Initialize socket connection — route to saved room DO when restoring session (Cloudflare prod)
  useEffect(() => {
    const saved = RoomSessionStorage.getSavedRoomData();
    const expired = saved.expiresAt ? Date.now() > saved.expiresAt : false;
    const roomFromUrl = getRoomCodeFromUrl();
    const hostFromUrl = getHostCodeFromUrl();
    const skipRestoreForHostCreate = isImplicitHostCreatePath(getCurrentPath());
    if (skipRestoreForHostCreate) {
      RoomSessionStorage.clearStaleNonPremiumRoomSession();
    }
    let roomForRouting =
      saved.roomName && !expired && (saved.hostToken || saved.playerName) && !skipRestoreForHostCreate
        ? saved.roomName
        : hostFromUrl || roomFromUrl || undefined;

    // /host deep link: connect directly to a new room DO (skip default DO entirely)
    if (!roomForRouting && isHostEntryPath(getCurrentPath())) {
      const savedExpired = saved.expiresAt ? Date.now() > saved.expiresAt : false;
      const hasRestorableSession =
        !!saved.roomName &&
        !savedExpired &&
        (!!saved.hostToken || !!saved.playerName);
      if (!hasRestorableSession || skipRestoreForHostCreate) {
        const code = generateRoomCode();
        const defaultGameType = config.games[0]?.id || 'trivia';
        pendingCreateRoomRef.current = { gameType: defaultGameType, roomCode: code };
        roomForRouting = code;
        autoHostAttemptedRef.current = true;
      }
    }

    const newSocket = createWebSocketAdapter(BACKEND_URL, roomForRouting);
    setSocket(newSocket);

    // Check if socket is already connected (can happen with Socket.io)
    // This helps in test scenarios and when connection is very fast.
    // Do NOT set myPlayerId from socket.id — stable id comes from room-joined / room-state
    // (setting early defeats RoomLoadingShell and can bind the wrong “me” after reconnect).
    if (newSocket.connected) {
      setConnected(true);
      // Clear any existing connection error on successful connection
      setConnectionError(null);
      connectionErrorAttemptsRef.current = 0;
    }

    // Set up timeout to detect persistent connection failures
    // If we're not connected after 10 seconds, show error
    let connectionTimeout: NodeJS.Timeout | null = null;
    
    // Only set timeout if not already connected
    if (!newSocket.connected) {
      connectionTimeout = setTimeout(() => {
        // Check again if still not connected (check socket directly, not React state)
        if (!newSocket.connected && !isLeavingRef.current) {
          console.warn('Connection timeout - server may be unreachable');
          connectionErrorAttemptsRef.current += 1;
          if (connectionErrorAttemptsRef.current >= 2) {
            setConnectionError(t('app.connectionError'));
          }
        }
      }, 10000); // 10 seconds
    }

    return () => {
      if (connectionTimeout) {
        clearTimeout(connectionTimeout);
      }
      newSocket.close();
    };
  }, []); // Empty deps - only run on mount

  // Sync connected state with socket's actual connection status
  // This handles cases where socket connects very quickly before connect event fires
  useEffect(() => {
    if (!socket) return;

    // Check connection status periodically (every 100ms) until connected
    // This helps in test scenarios where connection might be very fast
    const checkInterval = setInterval(() => {
      if (socket.connected && !connected) {
        setConnected(true);
        clearInterval(checkInterval);
      }
    }, 100);

    // Also check immediately
    if (socket.connected && !connected) {
      setConnected(true);
    }

    // Clean up interval after 5 seconds (connection should have happened by then)
    const timeout = setTimeout(() => {
      clearInterval(checkInterval);
    }, 5000);

    return () => {
      clearInterval(checkInterval);
      clearTimeout(timeout);
    };
  }, [socket, connected]);

  // Setup all socket event handlers
  useSocketEvents({
    socket,
    roomState,
    setRoomState,
    setConnected,
    setMyPlayerId,
    setIsHost,
    setShowJoinRoom,
    showJoinRoom,
    setReconnecting,
    setReconnectAttempts,
    roomStateRef,
    isLeavingRef,
    suppressSessionRestoreRef,
    needsRejoinRef,
    reconnectTimeoutRef,
    pendingStartGameRef,
    isHost,
    showToast,
    startReconnection,
    clearReconnection,
    connectionErrorAttemptsRef,
    setConnectionError,
    failedRoomsRef,
    setRestoreAttemptSettled,
    onReconnectTo: handleReconnectToRoom,
    onScheduleCreateRoom: scheduleCreateRoom,
    onHostDeepLinkSettled,
  });

  const handleCreateRoom = () => {
    isLeavingRef.current = false;
    suppressSessionRestoreRef.current = false;
    autoHostAttemptedRef.current = false;
    RoomSessionStorage.clearStaleNonPremiumRoomSession();
    setRestoreAttemptSettled(true);
    setCreatingHostFromUrl(true);
    navigateToHost();
    const defaultGameType = config.games[0]?.id || 'trivia';
    autoHostAttemptedRef.current = true;
    scheduleCreateRoom({ gameType: defaultGameType, roomCode: generateRoomCode() });
  };

  const handleJoinRoom = () => {
    isLeavingRef.current = false;
    suppressSessionRestoreRef.current = false;
    setIsPremiumJoin(false);
    setShowJoinRoom(true);
  };

  const handlePremiumJoinRoom = () => {
    isLeavingRef.current = false;
    suppressSessionRestoreRef.current = false;
    // Clear old room data when navigating to premium join
    // This prevents reconnection attempts to old non-premium rooms
    const savedData = RoomSessionStorage.getSavedRoomData();
    if (savedData.roomName && !savedData.expiresAt) {
      // Clear non-premium room data (rooms without expiration are non-premium)
      // This prevents conflicts when creating a new premium room
      console.log(`Clearing old non-premium room data (${savedData.roomName}) when navigating to premium join`);
      RoomSessionStorage.clearAll();
    }
    // Set premium join flag and show join room screen (not host setup!)
    setIsPremiumJoin(true);
    setShowJoinRoom(true);
  };

  const handlePremiumHostCreate = (roomCode: string) => {
    // Navigate to host creation URL
    window.location.href = `/?host=${roomCode}`;
  };

  const handleBackFromPremiumHostSetup = () => {
    setShowPremiumHostSetup(false);
  };

  const handlePhoneOnlyPlay = () => {
    setShowPhoneOnlySetup(true);
  };

  const handlePhoneOnlyContinue = (playerName: string, avatar: string) => {
    if (!socket) {
      showToast(t('app.notConnected'), 'error');
      return;
    }
    
    if (!connected) {
      showToast(t('app.notConnected'), 'error');
      return;
    }

    // Store player info for when room is created
    setPhoneOnlyPlayerInfo({ name: playerName, avatar });
    
    // Create room on the room's DO (not default) with a pre-generated code
    const defaultGameType = config.games[0]?.id || 'trivia';
    scheduleCreateRoom({ gameType: defaultGameType, roomCode: generateRoomCode() });
    
    // Hide phone-only setup screen
    setShowPhoneOnlySetup(false);
  };

  const handleBackFromPhoneOnlySetup = () => {
    setShowPhoneOnlySetup(false);
  };

  const handleJoinRoomSuccess = () => {
    // Room state will be set by the socket event handlers
    // This callback just hides the join room screen
  };

  const handleBackFromJoinRoom = () => {
    // Back to setup is intentional navigation — allow a later Host / Have a code? create
    isLeavingRef.current = false;
    // Keep suppressSessionRestoreRef: we left a room (or never had one); only explicit
    // create/join handlers should re-enable auto-restore / ?host= deep-link create.
    setShowJoinRoom(false);
    setIsPremiumJoin(false);
    navigateToRoot(true);
  };

  const handleGameSelected = (gameType: string) => {
    // Keep game selection screen open, just update the game type
    // Room state will be updated via socket events
  };

  const handleBackToGameSelection = () => {
    // Back to lobby is not leaving the room — clear stale flag so room-state updates apply
    isLeavingRef.current = false;
    // Request backend to return to waiting state
    if (socket && roomState) {
      socket.emit('return-to-waiting', { roomName: roomState.roomName });
      setTimeout(() => {
        socket.emit('get-room-state', { roomName: roomState.roomName });
      }, 100);
    }
  };

  const handleStartGame = (selectedGameType?: string) => {
    console.log('🚀 handleStartGame called', { 
      selectedGameType, 
      roomStateGameType: roomState?.gameType,
      isPremium: roomState?.isPremium,
      roomName: roomState?.roomName
    });
    
    if (socket && roomState) {
      // Get isPremium from room state
      const isRoomPremium = Boolean(roomState.isPremium);
      // Use the game type passed from UI (what user sees as selected) or fall back to roomState
      const gameTypeToCheck = selectedGameType || roomState.gameType;
      
      console.log('🔍 Checking game type for premium status', {
        gameTypeToCheck,
        isRoomPremium,
        roomStateGameType: roomState.gameType,
        selectedGameType
      });
      
      // If room is NOT premium, check if selected game requires premium
      if (!isRoomPremium && gameTypeToCheck) {
        // Find the game config to check if it's premium
        const selectedGame = ALL_GAMES.find(g => g.id === gameTypeToCheck);
        
        const requiresPremium =
          selectedGame?.premium === true && !isMonthlyFreeGame(gameTypeToCheck);

        console.log('🎮 Game config found', {
          gameTypeToCheck,
          found: !!selectedGame,
          isPremium: selectedGame?.premium,
          isMonthlyFree: isMonthlyFreeGame(gameTypeToCheck),
          requiresPremium,
          gameName: selectedGame?.name
        });
        
        // Premium games still require payment unless they are free this month
        if (requiresPremium) {
          console.log('❌ Premium game detected in non-premium room, redirecting to premium page', { 
            gameTypeToCheck, 
            isRoomPremium,
            roomStateGameType: roomState.gameType,
            gameName: selectedGame?.name
          });
          setShowPremiumPage(true);
          return; // CRITICAL: Return early to prevent game from starting
        }
      }
      
      console.log('✅ Game is allowed, starting game', {
        gameTypeToCheck,
        isRoomPremium,
        roomStateGameType: roomState.gameType
      });
      
      // Ensure room gameType matches what the host selected (covers race / missed change-game-type)
      const gameTypeToStart = selectedGameType || roomState.gameType;
      if (gameTypeToStart && gameTypeToStart !== roomState.gameType) {
        console.log('📤 Syncing game type before start', {
          from: roomState.gameType,
          to: gameTypeToStart,
        });
        socket.emit('change-game-type', { roomName: roomState.roomName, gameType: gameTypeToStart });
      }

      pendingStartGameRef.current = { gameType: gameTypeToStart || 'trivia' };
      // Include hostToken so backend can restore host if socketId changed (reconnect / other tab)
      const savedData = RoomSessionStorage.getSavedRoomData();
      const hostToken = savedData?.hostToken || undefined;
      socket.emit('start-game', { roomName: roomState.roomName, hostToken });
    } else {
      console.error('❌ Cannot start game: missing socket or roomState', {
        hasSocket: !!socket,
        hasRoomState: !!roomState
      });
    }
  };

  /** Called by LoadingQuestion after 10s with no response: try to recover (game selection / waiting for host) or go home after 5s more */
  const handleLoadingTimeout = useCallback(() => {
    const savedData = RoomSessionStorage.getSavedRoomData();
    const sessionValid =
      savedData.roomName &&
      !RoomSessionStorage.isRoomExpired();
    if (sessionValid && socket && savedData.roomName) {
      if (savedData.hostToken) {
        socket.emit('return-to-waiting', { roomName: savedData.roomName });
        setTimeout(() => {
          socket.emit('get-room-state', { roomName: savedData.roomName, hostToken: savedData.hostToken });
        }, 100);
      } else {
        socket.emit('get-room-state', { roomName: savedData.roomName });
      }
      // If still stuck after 5s, go home so user isn't left on "Taking longer than expected"
      setTimeout(() => {
        handleGoHomeRef.current();
      }, 5000);
    } else {
      handleGoHomeRef.current();
    }
  }, [socket]);

  const handleGoHome = () => {
    const isActiveHost = Boolean(socket && roomState && isHost);
    if (isActiveHost && !confirmLeaveOpen) {
      setConfirmLeaveOpen(true);
      return;
    }
    executeGoHome();
  };

  const executeGoHome = () => {
    setConfirmLeaveOpen(false);
    // Mark that we're intentionally leaving - prevents stray room-state events from re-establishing
    isLeavingRef.current = true;
    suppressSessionRestoreRef.current = true;
    needsRejoinRef.current = false;
    // Cancel any in-flight ?host= / /host create so leave cannot race into a new lobby
    pendingCreateRoomRef.current = null;
    setRestoreAttemptSettled(true);
    
    // Clear any pending reconnection timeouts
    clearReconnection();
    // Clear transient connection UI so routing back to root can render RoomSetup immediately.
    setConnectionError(null);

    const leavingRoomName = roomState?.roomName;
    
    // Always wipe session on intentional leave so connect/get-room-state cannot bounce
    // back into the lobby. Have a code? still works by typing the code (full navigation).
    RoomSessionStorage.clearAll();
    roomStateRef.current = null;
    setRoomState(null);
    setIsHost(false);
    setShowJoinRoom(false);
    setCreatingHostFromUrl(false);
    pendingStartGameRef.current = null;
    historyGuardActiveRef.current = false;
    autoHostAttemptedRef.current = false;
    setConnected(true);

    // Land on a clean root URL and pin a setup history entry so browser Back cannot
    // resurrect a stale ?host= deep-link and recreate the room (history loop).
    clearQueryParamFromUrl('host');
    clearEntryRouteFromUrl();
    window.history.replaceState({ toyoAtSetup: true }, '', ENTRY_ROUTES.ROOT);
    window.history.pushState({ toyoAtSetup: true }, '', ENTRY_ROUTES.ROOT);
    window.dispatchEvent(new Event(ENTRY_ROUTE_CHANGE_EVENT));

    if (socket && leavingRoomName) {
      socket.emit('leave-room', { roomName: leavingRoomName });
    }

    setTimeout(() => {
      isLeavingRef.current = false;
    }, 2000);
  };
  handleGoHomeRef.current = handleGoHome;

  // While in a room, trap browser Back so we confirm leave instead of navigating to a stale ?host= entry
  useEffect(() => {
    if (!roomState) {
      historyGuardActiveRef.current = false;
      return;
    }

    // Strip ?host= from the current entry, then push one guard entry (clean URL only)
    clearQueryParamFromUrl('host');
    if (!historyGuardActiveRef.current) {
      window.history.pushState(
        { toyoInRoom: true },
        '',
        window.location.pathname + window.location.search
      );
      historyGuardActiveRef.current = true;
    }

    const onPopState = () => {
      if (!roomStateRef.current || suppressSessionRestoreRef.current) {
        return;
      }
      // Stay in the lobby UI: re-arm with a clean URL (never re-introduce ?host=)
      clearQueryParamFromUrl('host');
      window.history.pushState(
        { toyoInRoom: true },
        '',
        window.location.pathname + window.location.search
      );
      historyGuardActiveRef.current = true;
      if (isHost) {
        setConfirmLeaveOpen(true);
      } else {
        handleGoHomeRef.current();
      }
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [roomState, isHost]);

  // After leave: if browser Back resurrects ?host= / /host, scrub it and stay on setup
  useEffect(() => {
    const scrubStaleHostDeepLink = () => {
      if (roomStateRef.current) return;
      if (!suppressSessionRestoreRef.current && !isLeavingRef.current) return;
      if (!getHostCodeFromUrl() && !isHostEntryPath()) return;
      console.log('Scrubbing stale host deep-link after intentional leave');
      clearQueryParamFromUrl('host');
      clearEntryRouteFromUrl();
      window.history.pushState({ toyoAtSetup: true }, '', ENTRY_ROUTES.ROOT);
      window.dispatchEvent(new Event(ENTRY_ROUTE_CHANGE_EVENT));
      setCreatingHostFromUrl(false);
      setShowJoinRoom(false);
    };

    window.addEventListener('popstate', scrubStaleHostDeepLink);
    return () => window.removeEventListener('popstate', scrubStaleHostDeepLink);
  }, []);

  // Drop host-deep-link loader once we have room state.
  // Do NOT clear suppressSessionRestoreRef here — intentional leave must stay suppressed
  // until the user explicitly clicks Host / Join / Have a code? Otherwise a bounced
  // room-state would unlock restore and loop forever.
  useEffect(() => {
    if (roomState) {
      setCreatingHostFromUrl(false);
      return;
    }
    // Room left/destroyed outside Leave confirm — don't reopen the modal on next lobby
    setConfirmLeaveOpen(false);
  }, [roomState]);

  // Safety: never leave the user stuck on "Creating your room" if room-state never arrives
  useEffect(() => {
    if (!creatingHostFromUrl || roomState) return;
    const timer = setTimeout(() => {
      if (roomStateRef.current) return;
      console.warn('Host deep-link loader timed out — returning to setup');
      setCreatingHostFromUrl(false);
      clearQueryParamFromUrl('host');
      clearEntryRouteFromUrl();
      navigateToRoot(true);
      setRestoreAttemptSettled(true);
    }, 15000);
    return () => clearTimeout(timer);
  }, [creatingHostFromUrl, roomState]);

  // Handle phone-only flow: after room is created, join as player
  useEffect(() => {
    if (phoneOnlyPlayerInfo && roomState && isHost && socket && !hostIsPlaying) {
      // Room is created, now join as player
      socket.emit('join-room', {
        roomName: roomState.roomName,
        playerName: phoneOnlyPlayerInfo.name,
        avatar: phoneOnlyPlayerInfo.avatar,
      });
      setHostIsPlaying(true);
      // Clear phone-only info after joining
      setPhoneOnlyPlayerInfo(null);
    }
  }, [phoneOnlyPlayerInfo, roomState, isHost, socket, hostIsPlaying]);

  // Show connection status screens only when reconnecting or when connection failed
  if (!partyAgeVerified) {
    return (
      <PartyAgeGate
        onVerified={() => setPartyAgeVerified(true)}
        onDecline={() => setPartyAgeVerified(false)}
      />
    );
  }

  const leaveConfirmModal = (
    <ConfirmModal
      isOpen={confirmLeaveOpen}
      title={t('app.leaveRoom.title')}
      message={t('app.leaveRoom.message')}
      confirmLabel={t('app.leaveRoom.confirm')}
      cancelLabel={t('app.leaveRoom.cancel')}
      onConfirm={executeGoHome}
      onCancel={() => {
        setConfirmLeaveOpen(false);
      }}
    />
  );

  const withLeaveConfirm = (node: React.ReactNode) => (
    <>
      {leaveConfirmModal}
      {node}
    </>
  );

  // While initially connecting (!connected), show RoomSetup so the UI is visible and
  // e2e tests / users can see the page; handleCreateRoom shows a toast if they click before connected
  if (reconnecting) {
    return (
      <>
        {leaveConfirmModal}
        <ConnectionStatus status="reconnecting" reconnectAttempts={reconnectAttempts} onGoHome={handleGoHome} />
      </>
    );
  }

  if (connectionError) {
    return (
      <>
        {leaveConfirmModal}
        <ConnectionStatus status="error" errorMessage={connectionError} onGoHome={handleGoHome} />
      </>
    );
  }

  // Check if we're on payment success page (package purchase only — AI top-up uses ?host=&ai_topup=1)
  const urlParams = new URLSearchParams(window.location.search);
  const sessionId = urlParams.get('session_id');
  const isAiTopUpReturn =
    urlParams.get('ai_topup') === '1' || urlParams.get('ai_topup_canceled') === '1';
  if (!isAiTopUpReturn && (isPaymentSuccessPath(currentPath) || sessionId)) {
    const handleGoToRoom = (roomName: string) => {
      navigateTo(`${ENTRY_ROUTES.ROOT}?room=${encodeURIComponent(roomName)}`, { replace: true });
      setShowJoinRoom(true);
    };
    const handlePaymentGoHome = () => {
      navigateToRoot(true);
      setShowJoinRoom(false);
      setRoomState(null);
      setIsHost(false);
    };
    return <PaymentSuccess sessionId={sessionId || undefined} onGoToRoom={handleGoToRoom} onGoHome={handlePaymentGoHome} />;
  }

  const showRestoreLoader = !roomState && couldRestore && !restoreAttemptSettled && !suppressSessionRestoreRef.current;
  const showHostEntryLoader =
    !roomState &&
    !showRestoreLoader &&
    (isHostEntryPath(currentPath) || creatingHostFromUrl || !!getHostCodeFromUrl());

  if (!roomState && (showRestoreLoader || showHostEntryLoader)) {
    const loaderLabel =
      creatingHostFromUrl || getHostCodeFromUrl() || isHostEntryPath(currentPath)
        ? t('app.creatingYourRoom')
        : undefined;
    return <RoomLoadingShell label={loaderLabel} />;
  }

  const onJoinScreen = shouldShowJoinScreen(currentPath, showJoinRoom);

  if (!roomState) {
    if (showPhoneOnlySetup) {
      return <PhoneOnlySetup onContinue={handlePhoneOnlyContinue} onBack={handleBackFromPhoneOnlySetup} />;
    }
    if (showPremiumHostSetup) {
      return <PremiumHostSetup onBack={handleBackFromPremiumHostSetup} onCreateHost={handlePremiumHostCreate} />;
    }
    if (onJoinScreen) {
      if (!socket) {
        return <RoomLoadingShell />;
      }
      return <JoinRoom socket={socket} onJoinSuccess={handleJoinRoomSuccess} onBack={handleBackFromJoinRoom} isPremium={isPremiumJoin} />;
    }
    return (
      <RoomSetup
        onHost={handleCreateRoom}
        onJoin={handleJoinRoom}
        onPremiumJoinRoom={handlePremiumJoinRoom}
        onPhoneOnlyPlay={handlePhoneOnlyPlay}
      />
    );
  }

  // Map app variant to game type for premium page
  const getGameType = (): 'blitz' | 'team' | 'family' | 'party' => {
    const variantMap: Record<string, 'blitz' | 'team' | 'family' | 'party'> = {
      blitz: 'blitz',
      team: 'team',
      family: 'family',
      party: 'party',
      coop: 'blitz',
    };
    return variantMap[appVariant] || 'blitz';
  };

  // Show premium page if needed
  if (showPremiumPage && roomState && isHost) {
    return withLeaveConfirm(
      <MenuPageWrapper
        title="Premium"
        onMenuClick={() => {}}
        onBack={() => setShowPremiumPage(false)}
        gameType={getGameType()}
      >
        <PremiumPage gameType={getGameType()} />
      </MenuPageWrapper>
    );
  }

  // Simple render logic based on gameState - single source of truth
  if (roomState.gameState === 'waiting') {
    return withLeaveConfirm(
      <GameSelection
        roomName={roomState.roomName}
        currentGameType={roomState.gameType}
        votes={roomState.votes || {}}
        categoryVotes={roomState.categoryVotes || {}}
        players={roomState.players || []}
        teams={roomState.teams}
        onSelectGame={handleGameSelected}
        onStartGame={handleStartGame}
        socket={socket!}
        isHost={isHost}
        myPlayerId={myPlayerId || undefined}
        settings={roomState.settings}
        branding={roomState.branding}
        companyQuestions={roomState.companyQuestions}
        aiPromptUsage={roomState.aiPromptUsage}
        onGoHome={handleGoHome}
        hostIsPlaying={hostIsPlaying}
        onHostIsPlayingChange={setHostIsPlaying}
        isPremium={roomState.isPremium}
        onJoinRoom={() => {
          // Leave lobby for premium join — same teardown rules as executeGoHome (no token keep)
          const leavingRoomName = roomState?.roomName;
          const leaveAsHost = Boolean(isHost && leavingRoomName);

          isLeavingRef.current = true;
          suppressSessionRestoreRef.current = true;
          pendingCreateRoomRef.current = null;
          setRestoreAttemptSettled(true);
          clearQueryParamFromUrl('host');

          RoomSessionStorage.clearAll();
          roomStateRef.current = null;
          setRoomState(null);
          setIsHost(false);
          historyGuardActiveRef.current = false;

          setIsPremiumJoin(true);
          setShowJoinRoom(true);

          if (socket && leavingRoomName && leaveAsHost) {
            socket.emit('leave-room', { roomName: leavingRoomName });
          }

          setTimeout(() => {
            isLeavingRef.current = false;
          }, 2000);
        }}
      />
    );
  }

  if (roomState.gameState === 'playing' || roomState.gameState === 'finished') {
    if (isHost) {
      const withFeelingAlone = (node: React.ReactNode) => (
        <FeelingAloneAiProvider
          roomName={roomState.roomName}
          socket={socket!}
          players={roomState.players}
          gameType={roomState.gameType}
          questionNumber={roomState.currentQuestion?.questionNumber}
          isBonusLevel={!!roomState.isBonusLevel || !!roomState.bonusLevelResult}
          gameState={roomState.gameState}
        >
          {node}
        </FeelingAloneAiProvider>
      );

      // If host is playing (same device in same room), no music drawer and no sound effects
      if (hostIsPlaying) {
        if (!myPlayerId) {
          return withLeaveConfirm(<RoomLoadingShell />);
        }
        // Host-as-player: player UI + host controls (pause/skip/leave). No SoundDrawer (muted).
        // Hide host controls on end-game screen — leave/replay are already available there.
        return withLeaveConfirm(
          withFeelingAlone(
            <>
              <PlayerDisplay roomState={roomState} socket={socket!} myPlayerId={myPlayerId} onGoHome={handleGoHome} isHost={true} onBackToGameSelection={handleBackToGameSelection} isBonusLevel={roomState.isBonusLevel} />
              {roomState.gameState === 'playing' && (
                <HostControlDrawer
                  socket={socket!}
                  roomName={roomState.roomName}
                  isPaused={roomState.isPaused}
                  onLeaveRoom={handleBackToGameSelection}
                  clearOfSoundDrawer={false}
                />
              )}
              {roomState.isPaused && <GamePausedOverlay />}
            </>
          )
        );
      }
      const withSoundDrawer = (node: React.ReactNode) => withLeaveConfirm(
        withFeelingAlone(
          <>
            {node}
            {roomState.gameState === 'playing' && (
              <HostControlDrawer
                socket={socket!}
                roomName={roomState.roomName}
                isPaused={roomState.isPaused}
                onLeaveRoom={handleBackToGameSelection}
              />
            )}
            <SoundDrawer />
          </>
        )
      );
      // During bonus level (and still playing): show bonus host screen. When game has finished,
      // show main game end screen (GameDisplay) so host sees end screen immediately without refocus.
      // Keep bonus host visible during outro when isBonusLevel was cleared but result not yet consumed.
      const showBonusHost =
        roomState.gameState === 'playing' &&
        (roomState.isBonusLevel || !!roomState.bonusLevelResult);
      if (showBonusHost) {
        return withSoundDrawer(
          <BonusHostScreen
            roomState={roomState}
            socket={socket!}
            onBackToGameSelection={handleBackToGameSelection}
            onGoHome={handleGoHome}
          />
        );
      }
      // Host not playing - show host display (leaderboard + question only)
      return withSoundDrawer(
        <HostDisplay
          roomState={roomState}
          socket={socket!}
          onBackToGameSelection={handleBackToGameSelection}
          myPlayerId={myPlayerId || undefined}
          hostIsPlaying={hostIsPlaying}
          onGoHome={handleGoHome}
          onLoadingTimeout={handleLoadingTimeout}
        />
      );
    } else {
      if (!myPlayerId) {
        return withLeaveConfirm(<RoomLoadingShell />);
      }
      return withLeaveConfirm(
        <>
          <PlayerDisplay roomState={roomState} socket={socket!} myPlayerId={myPlayerId} onGoHome={handleGoHome} onLoadingTimeout={handleLoadingTimeout} isBonusLevel={roomState.isBonusLevel} />
          {roomState.isPaused && <GamePausedOverlay />}
        </>
      );
    }
  }

  // Fallback (shouldn't happen)
  return <div>{t('app.fallbackLoading')}</div>;
}

function App() {
  return (
    <ToastProvider>
      <AppContent />
    </ToastProvider>
  );
}

export default App;

